// frontend/src/pages/GestionUsuarioEditarModal.jsx

import React, { useState } from 'react';
import { toast } from 'react-toastify';

const GestionUsuarioEditarModal = ({ usuario, cuenta, onClose, onSave }) => {
  // Si hay cuenta seleccionada, usar su costo_empresa, sino usar el del usuario
  const costoInicial = cuenta?.costo_empresa || usuario.costo_empresa || '0.00';

  const [formData, setFormData] = useState({
    first_name: usuario.first_name || '',
    last_name: usuario.last_name || '',
    email: usuario.email || '',
    costo_empresa: costoInicial
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Manejar cambios en inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar formulario
  const validarFormulario = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'El apellido es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!validarFormulario()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      // Pasar user_cuenta_id si hay cuenta seleccionada
      await onSave(usuario.id, formData, cuenta?.user_cuenta_id || null);
      toast.success('Usuario actualizado exitosamente');
      onClose();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);

      // Manejar errores de validacion del backend
      if (error.details) {
        const backendErrors = {};
        Object.keys(error.details).forEach(key => {
          backendErrors[key] = error.details[key][0]; // Primer error de cada campo
        });
        setErrors(backendErrors);
      }

      toast.error(error.error || 'Error al actualizar usuario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-m" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="modal-header-icon">
              <i className="fas fa-user-edit"></i>
            </div>
            <div className="modal-header-text">
              <h2 className="modal-header-title">Editar Usuario</h2>
              <p className="modal-header-subtitle">
                @{usuario.username}
                {cuenta && (
                  <> - Cuenta: <strong>{cuenta.cuenta_nombre}</strong></>
                )}
              </p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>x</button>
        </div>

        {/* Formulario */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Indicador de cuenta si existe */}
          {cuenta && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#e0e7ff',
              borderRadius: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-building" style={{ color: '#4338ca' }}></i>
                <span style={{ fontWeight: '500', color: '#4338ca' }}>
                  Editando configuracion para: {cuenta.cuenta_nombre}
                </span>
              </div>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6366f1' }}>
                El costo empresa se guardara solo para esta cuenta
              </p>
            </div>
          )}

          {/* Username (readonly) */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Nombre de usuario
            </label>
            <input
              type="text"
              value={usuario.username}
              disabled
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          {/* Email */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="usuario@ejemplo.com"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: errors.email ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (!errors.email) {
                  e.target.style.borderColor = '#3b82f6';
                }
              }}
              onBlur={(e) => {
                if (!errors.email) {
                  e.target.style.borderColor = '#d1d5db';
                }
              }}
            />
            {errors.email && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                {errors.email}
              </p>
            )}
          </div>

          {/* First Name y Last Name en fila */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Nombre <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Juan"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: errors.first_name ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  if (!errors.first_name) {
                    e.target.style.borderColor = '#3b82f6';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.first_name) {
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
              />
              {errors.first_name && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                  {errors.first_name}
                </p>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Apellido <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Perez"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: errors.last_name ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  if (!errors.last_name) {
                    e.target.style.borderColor = '#3b82f6';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.last_name) {
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
              />
              {errors.last_name && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                  {errors.last_name}
                </p>
              )}
            </div>
          </div>

          {/* Costo Empresa */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Costo Empresa {cuenta && <span style={{ color: '#4338ca', fontSize: '0.75rem' }}>(para {cuenta.cuenta_nombre})</span>}
            </label>
            <input
              type="number"
              name="costo_empresa"
              value={formData.costo_empresa}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
              {cuenta
                ? `Costo mensual del usuario para la cuenta "${cuenta.cuenta_nombre}"`
                : 'Costo mensual del usuario para la empresa'
              }
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GestionUsuarioEditarModal;
